import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import { History } from 'history'

import {
  fetchConfiguration,
  deleteConfiguration,
  fetchPresignedS3UrlForGet,
} from '../../actions/configurationActions'
import { displayNotification } from '../../actions/notificationsActions'
import { selectConfiguration } from '../../reducers/configurationReducer'
import { Configuration } from '../../core/types'
import Breadcrumb from '../../components/Breadcrumb'
import LoadingSection from '../../components/loadingSection'
import { displayCamelCaseAsTitleCase } from '../../helpers/displayFormatHelper'

export interface Props {
  configuration: Configuration
  location: { key: any } & Location
  history: History
  fetchConfiguration: (id: string) => Promise<any>
  deleteConfiguration: (id: string) => Promise<any>
  match: { params: { id: string } }
}

interface State {
  loading: boolean
  logoUrl?: string
}

export class ConfigurationShow extends Component<Props, State> {
  state: State = { loading: true }

  componentDidMount() {
    this.loadConfiguration()
  }

  loadLogo = async (logoPath: string) => {
    const logoUrl = await fetchPresignedS3UrlForGet(logoPath)
    this.setState({ logoUrl })
  }

  handleDelete = async () => {
    const { deleteConfiguration, configuration, history } = this.props
    if (
      window.confirm(
        `Are you sure you want to delete configuration ${configuration.id}?`
      )
    ) {
      try {
        await deleteConfiguration(configuration.id)
        history.push('/configuration')
      } catch (error) {
        displayNotification('Failed to delete configuration', 'error', 10000)
      }
    }
  }

  loadConfiguration = async () => {
    const { fetchConfiguration, match } = this.props
    this.setState({ loading: true })
    try {
      const configuration = await fetchConfiguration(match.params.id)
      if (configuration && configuration.callCenterLogoPath) {
        this.loadLogo(configuration.callCenterLogoPath)
      }
    } finally {
      this.setState({ loading: false })
    }
  }

  displayLabel = (key: string) => {
    switch (key) {
    case 'androidCprEnabled':
      return 'Android CPR enabled?'
    case 'callCenterLogoPath':
      return 'Call Center Logo'
    case 'signaturePath':
      return 'Signature'
    case 'testMode':
      return 'Test Mode enabled?'
    case 'homeProtectionHandoff':
      return 'Home Protection enabled?'
    case 'homeProtectionHandoffApiKeyConfigured':
      return 'Home Protection API Key configured?'
    case 'blinker2OrgCode':
      return 'Blinker 2.0 Org Code'
    default:
      return displayCamelCaseAsTitleCase(key)
    }
  }

  render() {
    const { configuration } = this.props

    if (!configuration) {
      return null
    }

    const levels = [
      { label: 'Index ', path: '/' },
      { label: 'Configuration', path: '/configuration' },
    ]

    return (
      <>
        <Breadcrumb levels={levels} />
        <LoadingSection isLoading={this.state.loading}>
          <div className="index index__drive-america">
            <h1 className="index-table__header">Configuration</h1>
            <div className="index-table__actions">
              <Link
               to={{
                      pathname: `/configuration/${configuration.id}/logs`,
                      state: {
                        versions: configuration.versions,
                        configurationId: configuration.id,
                        callCenterName: configuration.callCenterName
                      },
                  }}
                  className="button button-drive-america link-drive-america__new-applicant"
                  style={{display: 'inline-flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center'}}
              >
                Configuration Logs
              </Link>
              <a
                href="/configuration"
                className="button button-drive-america link-drive-america link-drive-america__new-applicant"
              >
                Back
              </a>
              <a
                href={`/configuration/${this.props.configuration.id}/edit`}
                className="button button-drive-america link-drive-america link-drive-america__new-applicant"
              >
                Edit Configuration
              </a>
              <button
                onClick={this.handleDelete}
                className="button button-drive-america button--danger"
              >
                Delete Configuration
              </button>
            </div>
            <table className="index-table">
              <tbody className="index-table__body">
                {[
                  'callCenterLink',
                  'callCenterLogoPath',
                  'callCenterName',
                  ...Object.keys(configuration)
                    .filter(
                      (key) =>
                        ![
                          'callCenterLink',
                          'callCenterLogoPath',
                          'callCenterName',
                          'versions',
                          'availableTimezones',
                          'organization'
                        ].includes(key)
                    )
                    .sort((a, b) => a.localeCompare(b)),
                ].map((key) => {
                  const value = configuration[key]
                  return (
                    <tr key={key} className="index-table__row">
                      <td className="index-table__cell">{this.displayLabel(key)}</td>
                      <td className="index-table__cell">
                        {(() => {
                          if (key === 'callCenterLogoPath') {
                            if (this.state.logoUrl) {
                              return (
                                <img
                                  src={this.state.logoUrl}
                                  alt="Call Center Logo"
                                  style={{ maxWidth: '200px' }}
                                />
                              )
                            }
                            return value ? 'Loading...' : 'N/A'
                          } else if (key === 'callCenterLink') {
                            return (
                              <a
                                href={value as string}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {value as string}
                              </a>
                            )
                          } else if (key === 'signaturePath') {
                            return (
                              <img
                                src={configuration.signaturePath}
                                alt="Signature"
                                style={{ maxWidth: '200px' }}
                              />
                            )
                          } else if (
                            [
                              'androidCprEnabled',
                              'testMode',
                              'homeProtectionHandoff',
                              'homeProtectionHandoffApiKeyConfigured',
                            ].includes(key)
                          ) {
                            return value ? 'Yes' : 'No'
                          }

                          return value
                        })()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </LoadingSection>
      </>
    )
  }
}

export const mapStateToProps = (state, props) => {
  return {
    configuration: selectConfiguration(
      state.configurations,
      props.match.params.id
    ),
  }
}

const mapDispatchToProps = {
  fetchConfiguration,
  deleteConfiguration,
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ConfigurationShow)
)
